import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Field, FieldError, FormSummary, RequiredLegend, Req, focusFirstError, clean } from "@/components/landing/Field";
import { StarPicker } from "@/components/landing/Stars";
import { API, apiError } from "@/lib/api";

const Message = ({ icon: Icon, color, title, text, testid }) => (
  <div data-testid={testid} className="flex flex-col items-center text-center gap-4 py-6">
    <Icon className={`w-14 h-14 ${color}`} />
    <h1 className="font-display text-3xl font-extrabold text-slate-900">{title}</h1>
    <p className="text-slate-600">{text}</p>
    <Link to="/" className="btn-heat mt-2">Retour au site</Link>
  </div>
);

export default function ReviewPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [f, setF] = useState({ rating: 0, title: "", comment: "", display_name: "", consent: false });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    document.title = "Donner mon avis | ClimNova";
    axios.get(`${API}/reviews/invite/${token}`)
      .then(({ data }) => { setF((p) => ({ ...p, display_name: data.display_name })); setState("form"); })
      .catch((e) => { setError(apiError(e)); setState("invalid"); });
  }, [token]);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = clean({
      rating: !f.rating && "La note est obligatoire.",
      title: f.title.trim().length < 3 && "Le titre est obligatoire (3 caractères minimum).",
      comment: f.comment.trim().length < 20 && `Le commentaire est obligatoire : au moins 20 caractères (${f.comment.trim().length}/20).`,
      display_name: f.display_name.trim().length < 2 && "Le prénom et l'initiale du nom sont obligatoires.",
    });
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError(ev.currentTarget);
    setState("sending");
    setServerError("");
    try {
      await axios.post(`${API}/reviews/${token}`, f);
      setState("done");
    } catch (err) {
      setServerError(apiError(err));
      setState("form");
    }
  };

  const set = (k) => (ev) => {
    setF({ ...f, [k]: ev.target.value });
    if (errors[k]) setErrors(({ [k]: _, ...rest }) => rest);
  };

  return (
    <div data-testid="review-page">
      <Header />
      <main className="bg-ice-soft min-h-[70vh]">
        <div className="container-x max-w-2xl py-14">
          <div className="order-card p-6 sm:p-10">
            {state === "loading" && <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" />}
            {state === "invalid" && <Message testid="review-invalid" icon={AlertCircle} color="text-orange-600" title="Lien indisponible" text={error} />}
            {state === "done" && <Message testid="review-thank-you" icon={CheckCircle2} color="text-emerald-600" title="Merci pour votre avis !" text="Votre avis a bien été enregistré. Il sera publié après modération, conformément à notre charte des avis." />}
            {(state === "form" || state === "sending") && (
              <form onSubmit={submit} noValidate data-testid="review-form" className="flex flex-col gap-5">
                <div>
                  <h1 className="font-display text-3xl font-extrabold text-slate-900">Votre avis sur le Midea PortaSplit</h1>
                  <p className="text-slate-600 mt-2">Merci de prendre une minute pour partager votre expérience.</p>
                  <div className="mt-2"><RequiredLegend testId="review-required-legend" /></div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-slate-800">Note <Req /></span>
                  <div tabIndex={-1} aria-invalid={!!errors.rating} className={`self-start rounded-lg outline-none ${errors.rating ? "ring-2 ring-red-300 bg-red-50 px-1" : ""}`}>
                    <StarPicker value={f.rating} onChange={(rating) => { setF({ ...f, rating }); setErrors(({ rating: _, ...rest }) => rest); }} />
                  </div>
                  <FieldError id="review-rating" error={errors.rating} />
                </div>
                <Field id="review-title" label="Titre" required error={errors.title}>
                  <input id="review-title" data-testid="review-title-input" maxLength={100} value={f.title} onChange={set("title")} aria-invalid={!!errors.title} className="field-input" />
                </Field>
                <Field id="review-comment" label="Commentaire" required error={errors.comment}>
                  <textarea id="review-comment" data-testid="review-comment-input" rows={5} maxLength={2000} value={f.comment} onChange={set("comment")} aria-invalid={!!errors.comment} className="field-input resize-y" />
                </Field>
                <Field id="review-display_name" label="Prénom et initiale du nom" required error={errors.display_name}>
                  <input id="review-display_name" data-testid="review-name-input" maxLength={40} value={f.display_name} onChange={set("display_name")} aria-invalid={!!errors.display_name} className="field-input" />
                </Field>
                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                  <Checkbox data-testid="review-consent-checkbox" checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: v === true })} className="mt-0.5" />
                  <span>J'accepte que ClimNova puisse me recontacter au sujet de mon avis (facultatif).</span>
                </label>
                <FormSummary errors={errors} testId="review-form-summary" />
                {serverError && <div data-testid="review-server-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{serverError}</div>}
                <button type="submit" disabled={state === "sending"} data-testid="review-submit-button" className="btn-heat self-start disabled:opacity-70">
                  {state === "sending" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Publier mon avis"}
                </button>
                <p className="text-xs text-slate-500">Votre avis sera modéré avant publication. <Link to="/charte-des-avis" className="underline">Charte des avis</Link></p>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
